import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type {
  PineRequirementsTable,
  PineRequirementsTableRow,
} from "../../types/reportStrategyPine";
import { formatPineRequirementsColumnHeader } from "../../utils/pineIrUi";

function tableRowCells(row: PineRequirementsTableRow, columns?: string[]): string[] {
  if (columns?.length) {
    return columns.map((col) => {
      const key = col.toLowerCase().replace(/\s+/g, "_");
      return row[key] ?? row[col] ?? "";
    });
  }
  const preferred = ["requirement", "message", "action", "dashboard_sim", "tradingview_compose"];
  const keys = preferred.filter((k) => row[k] != null && row[k] !== "");
  if (keys.length) return keys.map((k) => row[k] ?? "");
  return Object.values(row).filter((v): v is string => typeof v === "string" && v.length > 0);
}

export function resolvePineRequirementsHeaderLabels(table: PineRequirementsTable): string[] {
  const columns =
    table.columns ??
    (table.rows?.[0]
      ? Object.keys(table.rows[0]).filter((k) => table.rows![0][k])
      : ["Requirement", "Details"]);

  return (
    table.columns?.map((c) => formatPineRequirementsColumnHeader(c)) ??
    (columns[0] === "requirement"
      ? ["Requirement", "Message"]
      : columns[0] === "action"
        ? [
            "Management action",
            formatPineRequirementsColumnHeader("dashboard_sim"),
            formatPineRequirementsColumnHeader("tradingview_compose"),
          ]
        : columns.map((c) => formatPineRequirementsColumnHeader(c)))
  );
}

export type PineRequirementsComparisonTableProps = {
  table: PineRequirementsTable;
  className?: string;
};

/** Static Tessera vs TradingView / policy management matrices — shadcn table chrome only. */
export function PineRequirementsComparisonTable({
  table,
  className,
}: PineRequirementsComparisonTableProps): React.ReactElement {
  const headerLabels = resolvePineRequirementsHeaderLabels(table);

  return (
    <div
      data-pine-requirements-comparison-table=""
      data-shadcn-data-table=""
      className={cn("w-full min-w-0 max-w-full overflow-x-auto", className)}
    >
      <Table className="w-max min-w-full">
        {headerLabels.length > 0 ? (
          <TableHeader>
            <TableRow>
              {headerLabels.map((label) => (
                <TableHead key={label} className="whitespace-nowrap">
                  {label.replace(/_/g, " ")}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        ) : null}
        <TableBody>
          {(table.rows ?? []).map((row, rowIndex) => {
            const cells = tableRowCells(row, table.columns);
            return (
              <TableRow key={rowIndex}>
                {cells.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    className="min-w-[10rem] max-w-[24rem] align-top whitespace-normal font-[family-name:var(--font-primary)] text-sm font-normal text-foreground"
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
